import { formatNumber } from "@/lib/format";

type Props = {
  readonly value: number;
  readonly currentLeverage: number;
  readonly onChange: (n: number) => void;
};

const FULL_CLOSE_SENTINEL = 0;

/**
 * Slider goes from 1× (no debt, collateral kept) up to the current leverage
 * (no change). A separate "Full close" button sets the discrete 0 sentinel
 * for "withdraw everything, repay everything" mode.
 */
export const TargetLeverageSlider = ({
  value,
  currentLeverage,
  onChange,
}: Props) => {
  const isFullClose = value === FULL_CLOSE_SENTINEL;
  const isDeleverage1x = !isFullClose && value === 1;
  const isKeepCurrent =
    !isFullClose && Math.abs(value - currentLeverage) < 0.025;
  const min = 1;
  const max = Math.max(1.05, currentLeverage);
  const step = 0.05;
  const display = isFullClose ? 0 : value;
  const pct = ((Math.min(value, max) - min) / Math.max(max - min, 1e-9)) * 100;
  const safePct = isFullClose ? 0 : Math.max(0, Math.min(100, pct));

  return (
    <section>
      <div className="flex items-baseline justify-between mb-4">
        <h3>Resulting leverage</h3>
        <div className="flex items-baseline gap-2">
          <span
            className="font-serif tabular"
            style={{
              fontSize: 32,
              fontWeight: 300,
              letterSpacing: "-0.02em",
              color: "var(--ink-1)",
            }}
          >
            {isFullClose ? "0" : formatNumber(display, 2)}
          </span>
          <span
            className="font-sans text-[11px] tracking-[0.16em] uppercase"
            style={{ color: "var(--ink-3)" }}
          >
            ×
          </span>
        </div>
      </div>

      <div className="relative h-[18px] flex items-center">
        <div
          className="absolute inset-x-0 h-[2px] rounded-full"
          style={{ background: "var(--line-3)" }}
        />
        <div
          className="pointer-events-none absolute left-0 h-[2px] rounded-full"
          style={{ width: `${safePct}%`, background: "var(--ink-1)" }}
        />
        <input
          type="range"
          aria-label="Resulting leverage"
          className="slider relative z-10"
          min={min}
          max={max}
          step={step}
          value={isFullClose ? min : Math.max(min, Math.min(max, value))}
          onChange={(e) => onChange(Number.parseFloat(e.target.value))}
        />
      </div>

      <div className="flex justify-between mt-3">
        <span
          className="font-mono text-[10px] tracking-[0.16em]"
          style={{ color: "var(--ink-4)" }}
        >
          1.00× · no debt
        </span>
        <span
          className="font-mono text-[10px] tracking-[0.16em]"
          style={{ color: "var(--ink-4)" }}
        >
          {formatNumber(max, 2)}× · current
        </span>
      </div>

      <div className="flex gap-2 mt-5">
        {(
          [
            { label: "Full close", active: isFullClose, onClick: () => onChange(FULL_CLOSE_SENTINEL) },
            { label: "Deleverage to 1×", active: isDeleverage1x, onClick: () => onChange(1) },
            { label: "Keep current", active: isKeepCurrent, onClick: () => onChange(currentLeverage) },
          ] as const
        ).map(({ label, active, onClick }) => (
          <button
            key={label}
            type="button"
            onClick={onClick}
            className="px-3 min-h-7 py-1 leading-tight rounded-[2px] text-[11px] tracking-[0.12em] uppercase transition-all border"
            style={{
              background: active ? "var(--ink-1)" : "transparent",
              color: active ? "var(--on-light)" : "var(--ink-2)",
              borderColor: active ? "var(--ink-1)" : "var(--line-2)",
              fontWeight: 500,
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </section>
  );
};
